import { __rest } from '../../../_virtual/_tslib.js';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../legacy-autocomplete.scss.js';
import HoneyBem from '../../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import { InnerRefPropType } from '../../../packages/props-utils/prop-types.js';
import { LegacyAutocompleteOption } from '../quarks/legacy-autocomplete-option.js';
import { LegacyAutocompleteOptionGroup, LegacyAutocompleteGroupName } from '../quarks/legacy-autocomplete-option-group.js';
import { useLegacyAutocompleteContext } from '../legacy-autocomplete-context.js';
import { useLegacyAutocompleteOptionsContext } from './legacy-autocomplete-options-context.js';
import { Injecss } from '../../../packages/injecss/injecss.js';

const bem = new HoneyBem('legacy-autocomplete', styles);
/**
 * @deprecated This component is deprecated since v12.0.0. It is being kept to ease the transition
 * to the new [Autocomplete controller](/#/Controllers/Autocomplete). If you are using this
 * component, please consider migrating to the new one, as it is easier to use, has more features
 * and has less bugs.
 */
function LegacyAutocompleteOptions(_a) {
  var {
      label,
      appearance = 'boxed',
      extraClasses = '',
      innerRef,
      onBlur = () => null,
      optionHasSubtitle = true,
      optionProps = {},
      optionsToDisplay = 3,
      highlightQuery,
      renderOption
    } = _a,
    rest = __rest(_a, ["label", "appearance", "extraClasses", "innerRef", "onBlur", "optionHasSubtitle", "optionProps", "optionsToDisplay", "highlightQuery", "renderOption"]);
  // autocomplete contexts
  const {
    containerId,
    focusedOption
  } = useLegacyAutocompleteContext();
  const {
    options,
    listBoxRef
  } = useLegacyAutocompleteOptionsContext();
  // dropdown height
  const [dropHeight, setDropHeight] = useState(0);
  // height of the option item (button)
  const [optionHeight, setOptionHeight] = useState(0);
  // storing refs for option items in order to gain control on focus if needed
  const optionRefs = useRef([]);
  // resets option refs for next render
  optionRefs.current = [];
  // set dropdown height depending on option height
  useEffect(() => {
    setDropHeight(optionHeight * optionsToDisplay);
  }, [optionsToDisplay, optionHeight]);
  // scroll options container if visually focused option is hidden
  useEffect(() => {
    var _a, _b, _c;
    if (focusedOption !== undefined && focusedOption >= 0 && optionRefs.current[focusedOption]) {
      const listRect = (_a = listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
      const itemRect = (_b = optionRefs.current[focusedOption]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
      if (listRect && itemRect && (itemRect.y + itemRect.height > listRect.y + listRect.height || itemRect.y < listRect.y)) {
        (_c = listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current) === null || _c === void 0 ? void 0 : _c.scroll(listRect.x, listBoxRef.current.scrollTop + (itemRect.bottom - listRect.height - listRect.top));
      }
    }
  }, [focusedOption]);
  // grouped options
  const optionGroups = options && options.map((option, optionIndex) => {
    if ('options' in option && option.options.length > 0) {
      return /*#__PURE__*/React.createElement(LegacyAutocompleteOptionGroup, {
        bem: bem,
        key: option.key,
        groupName: (/*#__PURE__*/React.createElement(LegacyAutocompleteGroupName, {
          bem: bem,
          id: "".concat(containerId, "-groupName-").concat(optionIndex)
        }, option.group))
      }, option.options.map(entry => (/*#__PURE__*/React.createElement(LegacyAutocompleteOption, Object.assign({
        bem: bem,
        key: entry.key || "".concat(option.key, "-").concat(entry.title),
        option: entry,
        optionHasSubtitle: optionHasSubtitle,
        renderOption: renderOption,
        highlightQuery: highlightQuery,
        ref: $el => {
          // populating an array of option item refs
          if ($el) {
            optionRefs.current.push($el);
          }
          return $el && setOptionHeight($el.getBoundingClientRect().height + 1);
        }
      }, optionProps, entry.rest)))));
    }
    return null;
  }).filter(item => item !== null);
  const ungroupedOptions = options && options.map(option => {
    if (!('options' in option)) {
      return /*#__PURE__*/React.createElement(LegacyAutocompleteOption, Object.assign({
        bem: bem,
        key: option.key || option.title,
        option: option,
        optionHasSubtitle: optionHasSubtitle,
        renderOption: renderOption,
        highlightQuery: highlightQuery,
        ref: $el => {
          // populating an array of option item refs
          if ($el) {
            optionRefs.current.push($el);
          }
          return $el && setOptionHeight($el.getBoundingClientRect().height + 1);
        }
      }, optionProps, option.rest));
    }
    return null;
  }).filter(item => item !== null);
  return /*#__PURE__*/React.createElement("div", Object.assign({
    id: "".concat(containerId, "-listbox"),
    "aria-label": label,
    style: {
      maxHeight: "".concat(dropHeight, "px")
    },
    className: bem.e('dropdown', {
      boxed: appearance === 'boxed',
      flat: appearance === 'flat'
    }, extraClasses),
    onBlur: onBlur,
    ref: node => {
      // @ts-expect-error null on the last ref
      reactUtils.assignInnerRefWithComponentRef(node, innerRef, listBoxRef);
    },
    role: "listbox"
  }, rest), optionGroups, (ungroupedOptions === null || ungroupedOptions === void 0 ? void 0 : ungroupedOptions.length) ? (/*#__PURE__*/React.createElement(LegacyAutocompleteOptionGroup, {
    bem: bem
  }, ungroupedOptions)) : null);
}
LegacyAutocompleteOptions.displayName = 'LegacyAutocompleteOptions';
LegacyAutocompleteOptions.propTypes = {
  appearance: PropTypes.oneOf(['boxed', 'flat']),
  extraClasses: PropTypes.string,
  innerRef: InnerRefPropType,
  label: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  optionHasSubtitle: PropTypes.bool,
  optionProps: PropTypes.shape({}),
  optionsToDisplay: PropTypes.number,
  renderOption: PropTypes.func,
  highlightQuery: PropTypes.string
};
var legacyAutocompleteOptions = Injecss(LegacyAutocompleteOptions, styles);

export { LegacyAutocompleteOptions, legacyAutocompleteOptions as default };
